/**
 * WEICHZEICHNER fuer den Planar-Spiegel — echter Gauss in zwei Durchgaengen.
 *
 * Warum (Jans Sichttest 21.09.2026, drei Varianten nebeneinander): die erste
 * Unschaerfe las die Verkleinerungsstufen (Mipmaps) der Spiegel-Textur und
 * streute 13 duenne Abtastungen darueber. Das Raster dieser Stufen steht FEST
 * in der Textur; wandert der Inhalt beim Scrollen darueber, schwankt die
 * Helligkeit von Bild zu Bild — „der weichgezeichnete Spiegel flackert", am
 * staerksten OHNE Auslauf, scharf dagegen ruhig. Ein Gauss mit mindestens
 * 1,5 Texeln Breite auf einem (bei starker Unschaerfe verkleinerten) Bild ist
 * in Bewegung ruhig — dieselbe Technik wie bei Leuchteffekten.
 *
 * Ablauf je Spiegel-Bild:
 *   0. VOR:    Farbe x Auslauf-Faktor (premultipliziert), Faktor in Alpha.
 *              Der Auslauf steckt damit VOR dem Weichzeichnen im Bild — kein
 *              heller Umriss um ausgeblendete Enden (vgl. #934).
 *   1. GAUSS waagerecht  (liest VOR auf der passenden Verkleinerungsstufe)
 *   2. GAUSS senkrecht
 * Der Spiegel-Shader liest das Ergebnis und fuellt mit (1 - Alpha) auf das
 * Auslauf-Ziel (Hintergrundfarbe) auf.
 */
import THREE from "../utils/THREE";

/** Gauss-Breite (Sigma) in Pixeln einer 512er-Bezugstextur; stetig ab 0. */
export function weichSigmaBezug(blur: number): number {
    const b = Math.max(0, Math.min(1, blur || 0));
    return 1.5 * (Math.pow(2, 4.2 * b) - 1);
}

/**
 * Verkleinerung + Gauss-Breite im Zielbild. Das Zielbild wird so gewaehlt, dass
 * Sigma dort zwischen 2 und 4 Texeln liegt: breit genug fuer ein ruhiges
 * Bild, schmal genug fuer 17 Abtastungen (± 8 = mindestens 2 Sigma).
 */
export function weichStufe(sigmaQuellePx: number): { teiler: number; lod: number; sigmaZiel: number } {
    const s = Math.max(0, sigmaQuellePx || 0);
    const lod = s >= 4 ? Math.floor(Math.log2(s / 2)) : 0;
    const teiler = Math.pow(2, lod);
    return { teiler, lod, sigmaZiel: s / teiler };
}

const VERTEX = /* glsl */ `
varying vec2 vUv;
void main() {
    vUv = uv;
    gl_Position = vec4( position.xy, 0.0, 1.0 );
}`;

const FRAG_VOR = /* glsl */ `
uniform sampler2D tQuelle;
uniform sampler2D tTiefe;
uniform mat4 uInvVP;
uniform vec4 uEbene;
uniform float uAuslauf;
uniform vec2 uTexel;
varying vec2 vUv;
// -1 = keine Geometrie an dieser Stelle
float fadeAn( vec2 uvS ) {
    if ( uAuslauf <= 0.0 ) return 1.0;
    float dS = texture2D( tTiefe, uvS ).r;
    if ( dS >= 0.99999 ) return -1.0;
    vec4 wpS = uInvVP * vec4( uvS * 2.0 - 1.0, dS * 2.0 - 1.0, 1.0 );
    float hS = abs( dot( uEbene.xyz, wpS.xyz / wpS.w ) + uEbene.w );
    return exp( -2.3 * hS / uAuslauf );
}
void main() {
    vec4 c = texture2D( tQuelle, vUv );
    float f = fadeAn( vUv );
    // Stelle OHNE Geometrie erbt den kleinsten Faktor der Nachbarn (1,5 Texel):
    // an der Silhouette traegt die Farbe (MSAA-aufgeloest) schon Objekt-Anteile,
    // die Tiefe oft noch nicht — sonst helle Fransen an ausgeblendeten Kanten.
    if ( f < 0.0 ) {
        float fMin = 1.0;
        float fn;
        fn = fadeAn( vUv + vec2( uTexel.x, 0.0 ) ); if ( fn >= 0.0 ) fMin = min( fMin, fn );
        fn = fadeAn( vUv - vec2( uTexel.x, 0.0 ) ); if ( fn >= 0.0 ) fMin = min( fMin, fn );
        fn = fadeAn( vUv + vec2( 0.0, uTexel.y ) ); if ( fn >= 0.0 ) fMin = min( fMin, fn );
        fn = fadeAn( vUv - vec2( 0.0, uTexel.y ) ); if ( fn >= 0.0 ) fMin = min( fMin, fn );
        f = fMin;
    }
    gl_FragColor = vec4( c.rgb * f, f );
}`;

const FRAG_GAUSS = /* glsl */ `
uniform sampler2D tBild;
uniform vec2 uSchritt;   // ein Zieltexel in uv, in Durchgangs-Richtung
uniform float uSigma;    // in Zieltexeln
uniform float uLod;      // Verkleinerungsstufe der Quelle (nur 1. Durchgang)
varying vec2 vUv;
void main() {
    vec4 summe = vec4( 0.0 );
    float gewicht = 0.0;
    float s2 = max( uSigma, 0.05 );
    s2 = 2.0 * s2 * s2;
    for ( int i = -8; i <= 8; i ++ ) {
        float fi = float( i );
        float w = exp( - fi * fi / s2 );
        summe += textureLod( tBild, vUv + uSchritt * fi, uLod ) * w;
        gewicht += w;
    }
    gl_FragColor = summe / gewicht;
}`;

export interface WeichEingabe {
    quelle: THREE.Texture;
    tiefe: THREE.Texture;
    invVP: THREE.Matrix4;
    ebene: THREE.Vector4;
    auslauf: number;
    /** Gauss-Breite in Pixeln der Spiegel-Textur */
    sigmaPx: number;
}

export class SpiegelWeichzeichner {
    private readonly breite: number;
    private readonly hoehe: number;
    private readonly szene = new THREE.Scene();
    private readonly kamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    private readonly flaeche: THREE.Mesh;
    private readonly matVor: THREE.ShaderMaterial;
    private readonly matGauss: THREE.ShaderMaterial;
    private readonly rtVor: THREE.WebGLRenderTarget;
    private rtA: THREE.WebGLRenderTarget | null = null;
    private rtB: THREE.WebGLRenderTarget | null = null;
    private teiler = 0;

    constructor(breite: number, hoehe: number) {
        this.breite = Math.max(1, Math.round(breite));
        this.hoehe = Math.max(1, Math.round(hoehe));
        const basis = { depthTest: false, depthWrite: false, toneMapped: false } as const;
        this.matVor = new THREE.ShaderMaterial({
            ...basis,
            vertexShader: VERTEX,
            fragmentShader: FRAG_VOR,
            uniforms: {
                tQuelle: { value: null },
                tTiefe: { value: null },
                uInvVP: { value: new THREE.Matrix4() },
                uEbene: { value: new THREE.Vector4(0, 1, 0, 0) },
                uAuslauf: { value: 0 },
                uTexel: { value: new THREE.Vector2(1.5 / this.breite, 1.5 / this.hoehe) },
            },
        });
        this.matGauss = new THREE.ShaderMaterial({
            ...basis,
            vertexShader: VERTEX,
            fragmentShader: FRAG_GAUSS,
            uniforms: {
                tBild: { value: null },
                uSchritt: { value: new THREE.Vector2() },
                uSigma: { value: 1 },
                uLod: { value: 0 },
            },
        });
        this.flaeche = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.matVor);
        this.flaeche.frustumCulled = false;
        this.szene.add(this.flaeche);
        // VOR-Bild in voller Groesse MIT Verkleinerungsstufen: der erste Gauss-
        // Durchgang liest bei starker Unschaerfe eine grobere Stufe (Vorfilter).
        this.rtVor = new THREE.WebGLRenderTarget(this.breite, this.hoehe, {
            type: THREE.HalfFloatType,
            format: THREE.RGBAFormat,
            depthBuffer: false,
            generateMipmaps: true,
            minFilter: THREE.LinearMipmapLinearFilter,
            magFilter: THREE.LinearFilter,
        });
    }

    private zielbilder(teiler: number) {
        if (this.teiler === teiler && this.rtA && this.rtB) return;
        this.rtA?.dispose();
        this.rtB?.dispose();
        const b = Math.max(8, Math.ceil(this.breite / teiler));
        const h = Math.max(8, Math.ceil(this.hoehe / teiler));
        const opt = {
            type: THREE.HalfFloatType,
            format: THREE.RGBAFormat,
            depthBuffer: false,
            generateMipmaps: false,
            minFilter: THREE.LinearFilter,
            magFilter: THREE.LinearFilter,
        } as const;
        this.rtA = new THREE.WebGLRenderTarget(b, h, opt);
        this.rtB = new THREE.WebGLRenderTarget(b, h, opt);
        this.teiler = teiler;
    }

    /** Zeichnet die drei Durchgaenge; liefert die weichgezeichnete Textur. */
    render(renderer: THREE.WebGLRenderer, e: WeichEingabe): THREE.Texture {
        const stufe = weichStufe(e.sigmaPx);
        this.zielbilder(stufe.teiler);
        const rtA = this.rtA as THREE.WebGLRenderTarget;
        const rtB = this.rtB as THREE.WebGLRenderTarget;

        // Zustand des laufenden Hauptbilds sichern (wir stecken in dessen
        // onBeforeRender, wie der Reflector selbst).
        const altZiel = renderer.getRenderTarget();
        const altXr = renderer.xr.enabled;
        const altSchatten = renderer.shadowMap.autoUpdate;
        renderer.xr.enabled = false;
        renderer.shadowMap.autoUpdate = false;
        try {
            // 0. VOR
            const v = this.matVor.uniforms;
            v.tQuelle.value = e.quelle;
            v.tTiefe.value = e.tiefe;
            (v.uInvVP.value as THREE.Matrix4).copy(e.invVP);
            (v.uEbene.value as THREE.Vector4).copy(e.ebene);
            v.uAuslauf.value = e.auslauf;
            this.flaeche.material = this.matVor;
            renderer.setRenderTarget(this.rtVor);
            renderer.render(this.szene, this.kamera);

            // 1. waagerecht
            const g = this.matGauss.uniforms;
            this.flaeche.material = this.matGauss;
            g.uSigma.value = stufe.sigmaZiel;
            g.tBild.value = this.rtVor.texture;
            g.uLod.value = stufe.lod;
            (g.uSchritt.value as THREE.Vector2).set(1 / rtA.width, 0);
            renderer.setRenderTarget(rtA);
            renderer.render(this.szene, this.kamera);

            // 2. senkrecht
            g.tBild.value = rtA.texture;
            g.uLod.value = 0;
            (g.uSchritt.value as THREE.Vector2).set(0, 1 / rtB.height);
            renderer.setRenderTarget(rtB);
            renderer.render(this.szene, this.kamera);
        } finally {
            renderer.xr.enabled = altXr;
            renderer.shadowMap.autoUpdate = altSchatten;
            renderer.setRenderTarget(altZiel);
        }
        return rtB.texture;
    }

    dispose() {
        this.rtVor.dispose();
        this.rtA?.dispose();
        this.rtB?.dispose();
        this.matVor.dispose();
        this.matGauss.dispose();
        this.flaeche.geometry.dispose();
    }
}
